import { Fragment, useMemo, useRef } from 'react';

import { Button } from '@components/button';
import { UploadedFile } from '@components/file-field';
import { createForm, useCreateFormStore } from '@components/form';
import { isV2Form } from '@components/form-builder';
import { FormLabel } from '@components/form-label';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconSms } from '@components/icons/phone-sms';
import { IconRedo } from '@components/icons/redo';
import { IconSend } from '@components/icons/send';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { InfoButton, Sidebar, hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingEditorValue,
  FieldMappingGroup,
  FieldMappingVariables,
} from '@components/text-editors/addons/field-mapping';
import { EditorMethods, EditorValue } from '@components/text-editors/text-editor';
import { useClipboard } from '@hooks/use-clipboard';
import { useEvent } from '@hooks/use-event';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { useEmailAddressOptions } from 'api/company/hooks';
import { createTravellerJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useForm, useJourneyActionFormByJourneyActionId } from 'api/journey-action-forms/hooks';
import {
  FormAnswer,
  FormAnswers,
  FormAnswersV2,
  FormFile,
  JourneyActionForm,
} from 'api/journey-action-forms/requests';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useRunJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { GROUP_ACTION_TYPES, TravellerJourneyActionDetailed } from 'api/types';
import { ACTION_TYPES, getActionIconColor } from 'ui/actions/action-config';
import { ActivityTimeline } from 'ui/actions/activity-timeline';
import {
  ActionProgressBar,
  FormSidebarFooter,
  SMSMessageCountWarning,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  TriggerMethod,
  isActionError,
  isActionNotStarted,
  useEditingContext,
} from 'ui/actions/common';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { useFeature } from 'ui/hooks';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { EditRemindersContent } from 'ui/reminders/edit-reminders';
import { RemindersContentDetails } from 'ui/reminders/view-reminders';

import { TemplateCardForm } from './form-card';
import { FormResponsePending } from './form-response-pending';
import { FormLinkMissing, Info, hasFieldMappedFormLink } from './journey-sidebar';
import { ResponseContentV2 } from './response-content-v2';

import { getFieldMappingId, toFormUrl } from '../helpers';
import { EmailDetailCard } from '../send-email/traveller-sidebar';
import { SmsDetailCard } from '../send-sms/traveller-sidebar';
import { EmailAddressOptions, SendFromEmailSelect } from '../shared/send-from-email-select';

const ACTION_TYPE = ACTION_TYPES.combo_send_form;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { isEditing } = useEditingContext();
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const emailAddressOptions = useEmailAddressOptions();
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const tabItems = action
    ? [
        {
          label: t('details'),
          content: <Content action={action} emailAddressOptions={emailAddressOptions} />,
        },
        {
          label: t('responses'),
          content: <ResponseContent action={action} />,
        },
        {
          label: t('activity'),
          tabCount: activityCount > 0 ? activityCount : undefined,
          content: <ActivityTimeline actionId={action.shortId} />,
        },
      ]
    : [];

  const tabsProps = {
    tabs: tabItems,
    defaultIndex: action && getActionStatus(action) === 'completed' ? 1 : 0,
  };

  if (travellerActionQuery.isLoading || emailAddressOptions.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !action) {
    return <div>{t('dataNotFound')}</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE} {...tabsProps}>
        {isEditing && (
          <InfoButton>
            <Info />
          </InfoButton>
        )}
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
    </Sidebar>
  );
};

type BaseContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
  methodsCount?: string;
  remindersCount?: string;
  journeyActionForm?: JourneyActionForm;
};

type ContentProps = BaseContentProps & {
  emailAddressOptions: EmailAddressOptions;
};

const Content = ({ action, emailAddressOptions }: ContentProps) => {
  const { data: journeyActionForm } = useJourneyActionFormByJourneyActionId(
    action.journeyActionId,
    action.travellerJourneyId
  );
  const methodsCount = (action.data.emailNotify ? 1 : 0) + (action.data.smsNotify ? 1 : 0);
  const reminders = action.reminderSchedules ?? [];
  const completedRemindersCount = reminders.filter(
    (reminder) => reminder.status === 'completed'
  ).length;
  const fractionString =
    reminders.length > 0 ? `${completedRemindersCount} / ${reminders.length}` : undefined;

  const { isEditing } = useEditingContext();

  if (isEditing) {
    return (
      <FormContent
        action={action}
        emailAddressOptions={emailAddressOptions}
        methodsCount={methodsCount.toString()}
        remindersCount={fractionString}
        journeyActionForm={journeyActionForm}
      />
    );
  }

  return (
    <DetailsContent
      action={action}
      methodsCount={methodsCount.toString()}
      remindersCount={fractionString}
      journeyActionForm={journeyActionForm}
    />
  );
};

type DetailsContentProps = BaseContentProps;

const DetailsContent = ({
  action,
  methodsCount,
  remindersCount,
  journeyActionForm,
}: DetailsContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { onCopy } = useClipboard();
  const { startEditing } = useEditingContext();
  const runMutation = useRunJourneyTravellerAction(action, {
    onSuccess: hideSidebar,
  });

  const emailAction = action.groupActions?.find((action) => action.action === 'send_email') as
    | TravellerJourneyActionDetailed<'send_email'>
    | undefined;
  const smsAction = action.groupActions?.find((action) => action.action === 'send_sms') as
    | TravellerJourneyActionDetailed<'send_sms'>
    | undefined;
  const formAction = action.groupActions?.find(
    (action) => (action.action as GROUP_ACTION_TYPES) === 'instantiate_form'
  ) as any;
  const isError = isActionError(action);
  const status = getActionStatus(action);

  const progressText =
    status === 'completed'
      ? t('formCompleted')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  if (!journeyActionForm) {
    return (
      <Stack space={4}>
        <Text font="semibold14" text={t('formMissingTitle')} />
        <Text font="regular14" text={t('formMissingMessage')} />
      </Stack>
    );
  }

  return (
    <>
      <Stack space={4}>
        <Stack space={6}>
          <ActionProgressBar
            action={action}
            progressText={progressText}
            isError={isError}
            isCard={false}
          />
          <Sidebar.Separator />
          <HideContainer title={t('actionDetails')}>
            <Stack space={4}>
              <LabelTextGroup label={t('description')} text={action.description} />
              <TemplateCardForm
                formId={journeyActionForm.shortId}
                name={journeyActionForm.name}
                isPreviewOnly
                formUrl={journeyActionForm.formUrl ?? undefined}
              />
            </Stack>
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer title={t('notificationMethod')} counter={methodsCount}>
            <Stack space={2}>
              {action.data.emailNotify && emailAction && <EmailDetailCard action={emailAction} />}
              {action.data.smsNotify && smsAction && <SmsDetailCard action={smsAction} />}
            </Stack>
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer title={t('reminders')} defaultClosed counter={remindersCount}>
            <RemindersContentDetails
              action={action}
              emailAction={emailAction}
              smsAction={smsAction}
            />
          </HideContainer>
          <Sidebar.Separator />
        </Stack>
      </Stack>

      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('sendForm')}
        retryText={t('resendForm')}
      />

      {action.status === 'in_progress' && !isError && (
        <Sidebar.Footer>
          <Stack orientation="horizontal" justify="space-between">
            <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
            <Stack orientation="horizontal" space={2} justify="flex-end">
              {formAction?.actionValues?.uniqueLink && (
                <IconButton
                  variant="secondary-purple"
                  icon={<IconTextFormatLink />}
                  onClick={() =>
                    onCopy(
                      toFormUrl(formAction?.actionValues?.uniqueLink ?? ''),
                      t('formLinkCopied')
                    )
                  }
                />
              )}
              <Button
                variant="secondary"
                iconLeft={<IconRedo />}
                text={t('resendForm')}
                onClick={() => runMutation.mutateAsync({})}
                tooltip={!action.isConfigured ? t('actionNotConfigured') : ''}
                isDisabled={runMutation.isLoading || !action.isConfigured}
              />
            </Stack>
          </Stack>
        </Sidebar.Footer>
      )}
    </>
  );
};

const ResponseContentV1 = ({ formAnswers }: { formAnswers: FormAnswers }) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const showFilePreviewModal = usePreviewFileModal();
  const formFiles: FormFile[] = [];
  formAnswers.forEach((ans) => {
    ans.answers.forEach((a) => {
      if (a.value !== null) {
        if (a.type === 'file') {
          formFiles.push(a.value);
        } else if (a.type === 'file-multi') {
          formFiles.push(...a.value);
        }
      }
    });
  });
  const renderSwitch = (ans: FormAnswer) => {
    switch (ans.type) {
      case 'dateField':
        return (
          <Text
            font="regular16"
            text={ans.value ? abbreviateDate(ans.value, 'en', 'datetime') : t('noAnswer')}
            color={ans.value ? 'textPrimary' : 'textHelper'}
          />
        );
      case 'file':
        return (
          <>
            {ans.value ? (
              <UploadedFile
                name={ans.value.name}
                path={ans.value.src}
                onPreview={() => {
                  showFilePreviewModal({ formFile: ans.value, filesList: formFiles });
                }}
              />
            ) : (
              <Text font="regular16" text={t('noAnswer')} color="textHelper" />
            )}
          </>
        );
      case 'file-multi':
        return ans.value ? (
          <Stack space={2}>
            {ans.value.map((file, index) => (
              <UploadedFile
                key={index}
                name={file.name}
                path={file.src}
                onPreview={() => {
                  showFilePreviewModal({ formFile: file, filesList: formFiles });
                }}
              />
            ))}
          </Stack>
        ) : (
          <Text font="regular16" text={t('noAnswer')} color="textHelper" />
        );
      default:
        return (
          <Text
            font="regular16"
            text={ans.value || t('noAnswer')}
            color={ans.value ? 'textPrimary' : 'textHelper'}
          />
        );
    }
  };

  return (
    <Stack space={4}>
      {formAnswers.map((field) => {
        return (
          <Fragment key={field.h3}>
            <Text font="bold18" text={field.h3} />
            {field.answers.map((ans, index) => {
              return (
                <div key={index}>
                  <FormLabel text={ans.label} />
                  {renderSwitch(ans)}
                </div>
              );
            })}
            <Separator />
          </Fragment>
        );
      })}
    </Stack>
  );
};

const ResponseContent = ({ action }: BaseContentProps) => {
  const formQuery = useForm(action.shortId);
  const isNotStarted = isActionNotStarted(action);

  if (formQuery.isLoading) {
    return <Spinner />;
  }

  if (!formQuery.isSuccess || isNotStarted || action.status === 'in_progress') {
    return (
      <div style={{ margin: pxToRem(-24) }}>
        <FormResponsePending />
      </div>
    );
  }

  if (isV2Form(formQuery.data.fieldsConfig)) {
    return <ResponseContentV2 formAnswers={formQuery.data.formAnswers as FormAnswersV2} />;
  }

  return <ResponseContentV1 formAnswers={formQuery.data.formAnswers as FormAnswers} />;
};

export type FormData = {
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  emailNotify: boolean;
  emailSendFromEmailId?: string;
  emailRecipient?: FieldMappingEditorValue;
  emailSubject?: FieldMappingEditorValue;
  emailMessage?: string;
  attachments?: FieldMappingEditorValue[];
  smsNotify: boolean;
  smsRecipient?: FieldMappingEditorValue;
  smsMessage: EditorValue<{ variables?: FieldMappingVariables }>;
  emailReminderSwitch: boolean;
  smsReminderSwitch: boolean;
  daysAfterSending: string;
  hour: string;
  minute: string;
  repeatSwitch: boolean;
  frequency: string;
  amount: string;
};

const Form = createForm<FormData>();

type FormContentProps = BaseContentProps & {
  emailAddressOptions: EmailAddressOptions;
};

const FormContent = ({
  action,
  emailAddressOptions,
  methodsCount,
  remindersCount,
  journeyActionForm,
}: FormContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const { isEnabled: hasSMSEnabled } = useFeature('FEATURE_SMS');
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues() as FormData;
  const isDirty = formStore.useIsDirty();
  const fieldMappingId = getFieldMappingId(action.journeyId);
  const baseVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['email'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['phone'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['file', 'file_multi'],
  });
  const uploadMutation = useUploadFile(createTravellerJourneyActionDocumentPath(action), true);

  const hasEmailNotify = formStore.useFieldValue('emailNotify');
  const hasSmsNotify = formStore.useFieldValue('smsNotify');
  const notifyError = !hasEmailNotify && !hasSmsNotify;

  const formAction = action.groupActions?.find(
    (action) => (action.action as GROUP_ACTION_TYPES) === 'instantiate_form'
  );

  const messageVariableGroups: FieldMappingGroup[] = useMemo(() => {
    return [
      ...baseVariableGroups,
      {
        id: formAction!.shortId,
        icon: <IconForm color={getActionIconColor(ACTION_TYPE)} />,
        label: t('form'),
        fields: [
          {
            action: formAction?.shortId,
            label: t('formLink'),
            source: 'action',
            field: 'unique_link',
            type: 'text',
          },
        ],
      },
    ];
  }, [baseVariableGroups, formAction, t]);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = useEvent(async (data: FormData) => {
    const hasReminder = data.emailReminderSwitch || data.smsReminderSwitch;
    const submissionData = {
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        emailNotify: data.emailNotify,
        smsNotify: data.smsNotify,
        ...(data.emailNotify && {
          sendEmail: {
            sendFromEmailId: data.emailSendFromEmailId,
            recipients: data.emailRecipient,
            subject: data.emailSubject,
            content: data.emailMessage,
            attachments: data.attachments,
          },
        }),
        ...(data.smsNotify && {
          sendSms: {
            recipients: data.smsRecipient,
            message: data.smsMessage,
          },
        }),
      },
      ...(hasReminder && {
        reminder: {
          travellerAction: action.shortId,
          emailNotify: data.emailReminderSwitch,
          smsNotify: data.smsReminderSwitch,
          ...(data.emailReminderSwitch && {
            emailData: {
              sendFromEmailId: data.emailSendFromEmailId,
              recipients: data.emailRecipient,
              content: data.emailMessage,
              subject: {
                value: `[Reminder] ${data.emailSubject?.value}`,
                variables: data.emailSubject?.variables,
              },
            },
          }),
          ...(data.smsReminderSwitch && {
            smsData: {
              recipients: data.smsRecipient,
              message: data.smsMessage,
            },
          }),
          days: data.daysAfterSending,
          hours: data.hour,
          minutes: data.minute,
          frequency: data.repeatSwitch ? data.frequency : null,
          amount: data.repeatSwitch ? data.amount : null,
        },
      }),
    };
    return editMutation.mutateAsync(submissionData);
  });

  const isFirstReminderCompleted =
    action.reminderSchedules?.[0] && action.reminderSchedules[0].status === 'completed';

  const isRepeatRemindersCompleted =
    action.status === 'in_progress' &&
    action.reminderSchedules?.every((reminder) => reminder.status === 'completed');

  const emailEditorRef = useRef<EditorMethods>(null);
  const emailMessage = formStore.useFieldValue('emailMessage') as FieldMappingEditorValue;
  const emailValue = emailMessage?.value ?? '';
  const emailLinkMissing =
    formValues.emailNotify && !hasFieldMappedFormLink(emailValue, formAction?.shortId);

  const smsEditorRef = useRef<EditorMethods>(null);
  const smsMessage = formStore.useFieldValue('smsMessage') as FieldMappingEditorValue;
  const smsValue = smsMessage?.value ?? '';
  const smsLinkMissing =
    formValues.smsNotify && !hasFieldMappedFormLink(smsValue, formAction?.shortId);

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <Stack space={4}>
            <DescriptionFormField defaultValue={action.description} />
            <TemplateCardForm
              formId={journeyActionForm?.shortId ?? ''}
              name={journeyActionForm?.name ?? ''}
            />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            hideLabel
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('notificationMethod')} counter={methodsCount}>
          <Stack space={4}>
            <Stack space={4} orientation="horizontal" align="center" justify="space-between">
              <Stack orientation="horizontal" space={2} align="center">
                <IconEmail color="elementPurple50" />
                <Text font="semibold14" text={t('email')} />
              </Stack>
              <Form.Switch
                defaultValue={Boolean(action.values?.emailNotify)}
                id="emailNotify"
                label={t('email')}
                labelVisible={false}
                onChange={(value) => {
                  !value && formStore.actions.setValue('emailReminderSwitch', false);
                }}
              />
            </Stack>
            <Form.ConditionalValue id="emailNotify" when={(value) => Boolean(value)}>
              <Stack space={4}>
                <SendFromEmailSelect
                  Form={Form}
                  emailAddressOptions={emailAddressOptions}
                  id="emailSendFromEmailId"
                  defaultValue={action.values?.sendEmail?.sendFromEmailId}
                />
                <Form.EmailListField
                  label={t('recipient')}
                  id="emailRecipient"
                  variableGroups={emailVariableGroups}
                  validators={['required']}
                  defaultValue={action.values?.sendEmail?.recipients}
                  fieldMappingId={fieldMappingId}
                />
                <Form.FieldMappingTextEditor
                  label={t('emailSubject')}
                  id="emailSubject"
                  plaintext
                  singleLine
                  variableGroups={baseVariableGroups}
                  validators={['required']}
                  defaultValue={action.values?.sendEmail?.subject}
                  fieldMappingId={fieldMappingId}
                />
                <Stack space={1}>
                  <Form.FieldMappingTextEditor
                    label={t('message')}
                    id="emailMessage"
                    variableGroups={messageVariableGroups}
                    validators={['required']}
                    defaultValue={action.values?.sendEmail?.content}
                    ref={emailEditorRef}
                    fieldMappingId={fieldMappingId}
                  />
                  {emailLinkMissing && (
                    <FormLinkMissing
                      editorRef={emailEditorRef}
                      formShortId={formAction?.shortId}
                      variableGroups={messageVariableGroups}
                    />
                  )}
                </Stack>
                <Form.FieldMappingFileListField
                  label={t('emailAttachment')}
                  id="attachments"
                  defaultValue={action.values?.sendEmail?.attachments}
                  variableGroups={fileVariableGroups}
                  onSelect={uploadMutation.mutateAsync}
                  fieldMappingId={fieldMappingId}
                />
              </Stack>
              <Separator style={{ margin: `${pxToRem(16)} 0 ${pxToRem(4)} 0` }} />
            </Form.ConditionalValue>
            {hasSMSEnabled && (
              <>
                <Stack space={4} orientation="horizontal" align="center" justify="space-between">
                  <Stack orientation="horizontal" space={2} align="center">
                    <IconSms color="elementPurple50" />
                    <Text font="semibold14" text={t('sms')} />
                  </Stack>
                  <Form.Switch
                    id="smsNotify"
                    label={t('sms')}
                    labelVisible={false}
                    defaultValue={Boolean(action.values?.smsNotify)}
                    onChange={(value) => {
                      !value && formStore.actions.setValue('smsReminderSwitch', false);
                    }}
                  />
                </Stack>
                <Form.ConditionalValue id="smsNotify" when={(value) => Boolean(value)}>
                  <Stack space={4}>
                    <Form.FieldMappingPhoneListField
                      label={t('recipient')}
                      id="smsRecipient"
                      variableGroups={phoneVariableGroups}
                      validators={['required']}
                      defaultValue={action.values?.sendSms?.recipients}
                      fieldMappingId={fieldMappingId}
                    />
                    <Stack space={1}>
                      <Form.FieldMappingTextEditor
                        plaintext
                        label={t('message')}
                        id="smsMessage"
                        variableGroups={messageVariableGroups}
                        validators={['required']}
                        ref={smsEditorRef}
                        defaultValue={action.values?.sendSms?.message}
                        fieldMappingId={fieldMappingId}
                      />
                      {smsLinkMissing && (
                        <FormLinkMissing
                          editorRef={smsEditorRef}
                          formShortId={formAction?.shortId}
                          variableGroups={messageVariableGroups}
                        />
                      )}
                    </Stack>
                    <SMSMessageCountWarning message={formValues?.smsMessage} />
                  </Stack>
                </Form.ConditionalValue>
              </>
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('reminders')} defaultClosed counter={remindersCount}>
          <EditRemindersContent
            action={action}
            formStore={formStore}
            isFirstReminderCompleted={isFirstReminderCompleted}
            isRepeatRemindersCompleted={isRepeatRemindersCompleted}
          />
        </HideContainer>
      </Stack>
      <FormSidebarFooter
        Form={Form}
        isDirty={isDirty}
        editMutation={editMutation}
        hasNotifyError={notifyError || emailLinkMissing || smsLinkMissing}
      />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    formLink: 'Form Link',
    sendForm: 'Send form',
    message: 'Message',
    details: 'Details',
    responses: 'Responses',
    activity: 'Activity',
    dataNotFound: 'Data not found',
    description: 'Description',
    recipient: 'Send to',
    formCompleted: 'Form completed',
    error: 'Error',
    inProgress: 'In progress',
    notStarted: 'Not started',
    actionNotConfigured: 'Action not configured',
    noAnswer: 'No answer provided',
    actionDetails: 'Action details',
    notificationMethod: 'Notification method',
    triggerMethod: 'Trigger method',
    email: 'Email',
    sms: 'SMS',
    form: 'Send form',
    reminders: 'Reminders',
    resendForm: 'Resend form',
    emailSubject: 'Email subject',
    emailAttachment: 'Email attachment',
    formLinkCopied: 'Form link copied',
    formMissingTitle: 'Form not found',
    formMissingMessage:
      'This action is missing its form. Please refresh the page and try again or contact Support.',
  },
};
